import React, { useState, useEffect } from 'react';
import { useAuth } from '../store';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = (() => {
  const h = window.location.hostname;
  return process.env.REACT_APP_API_URL || ((h === 'localhost' || h === '127.0.0.1') ? 'http://localhost:5001/api' : `http://${h}:5001/api`);
})();

interface User {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: 'super_admin' | 'admin_branch' | 'owner_branch' | 'cashier' | 'finance' | 'accounting';
  branch_id: string | null;
  branch_name?: string;
  status: 'active' | 'inactive';
  last_login: string | null;
  created_at: string;
}

interface Branch {
  id: string;
  name: string;
  code: string;
  city: string;
}

const ROLE_LABEL: Record<string, string> = {
  super_admin: 'Super Admin',
  admin_branch: 'Admin Cabang',
  owner_branch: 'Owner Cabang',
  cashier: 'Kasir',
  finance: 'Finance',
  accounting: 'Accounting',
};

const ROLE_COLOR: Record<string, string> = {
  super_admin: '#7C3AED',
  admin_branch: '#1A73E8',
  owner_branch: '#D97706',
  cashier: '#27AE60',
  finance: '#0891b2',
  accounting: '#be185d',
};

const ROLE_BG: Record<string, string> = {
  super_admin: '#EDE9FE',
  admin_branch: '#EEF4FF',
  owner_branch: '#FEF3C7',
  cashier: '#EAFAF1',
  finance: '#e0f2fe',
  accounting: '#fce7f3',
};

interface CreateForm { name: string; email: string; phone: string; password: string; role: string; branch_id: string; }
const EMPTY_FORM: CreateForm = { name: '', email: '', phone: '', password: '', role: 'cashier', branch_id: '' };

export const UsersPage: React.FC = () => {
  const { user: currentUser, token } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState<User[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterRole, setFilterRole] = useState('all');
  const [filterBranch, setFilterBranch] = useState('all');

  // Create modal
  const [createModal, setCreateModal] = useState(false);
  const [form, setForm] = useState<CreateForm>(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // Edit modal
  const [editModal, setEditModal] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({ name: '', role: '', branch_id: '', status: 'active' as 'active' | 'inactive' });
  const [saving, setSaving] = useState(false);

  // Reset password modal
  const [resetModal, setResetModal] = useState(false);
  const [resetUser, setResetUser] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!currentUser) navigate('/login');
  }, [currentUser]);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [usersRes, branchesRes] = await Promise.all([
        axios.get(`${API}/users`, { headers }).then(r => r.data.data || r.data).catch(() => []),
        axios.get(`${API}/branches`, { headers }).then(r => r.data.data || r.data).catch(() => []),
      ]);

      const branchMap: Record<string, string> = {};
      const branchList = Array.isArray(branchesRes) ? branchesRes : [];
      branchList.forEach((b: Branch) => { branchMap[b.id] = b.name; });
      setBranches(branchList);

      const userList = Array.isArray(usersRes) ? usersRes : [];
      setUsers(userList.map((u: User) => ({ ...u, branch_name: u.branch_id ? branchMap[u.branch_id] : undefined })));
    } catch (e) {
      console.error('Failed to fetch users', e);
    } finally {
      setLoading(false);
    }
  };

  // ── Create ──
  const openCreate = () => {
    setForm(EMPTY_FORM);
    setCreateError('');
    setCreateModal(true);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((form.role === 'admin_branch' || form.role === 'owner_branch') && !form.branch_id) {
      setCreateError('Admin Cabang & Owner Cabang wajib dipilihkan cabang');
      return;
    }
    setCreating(true);
    setCreateError('');
    try {
      await axios.post(`${API}/users`, {
        name: form.name,
        email: form.email,
        phone: form.phone || undefined,
        password: form.password,
        role: form.role,
        branch_id: form.branch_id || null,
      }, { headers });
      setCreateModal(false);
      fetchAll();
    } catch (err: any) {
      setCreateError(err?.response?.data?.error || 'Gagal membuat user');
    } finally {
      setCreating(false);
    }
  };

  // ── Edit ──
  const openEdit = (u: User) => {
    setEditUser(u);
    setEditForm({ name: u.name, role: u.role, branch_id: u.branch_id || '', status: u.status });
    setEditModal(true);
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editUser) return;
    setSaving(true);
    try {
      await axios.put(`${API}/users/${editUser.id}`, {
        name: editForm.name,
        role: editForm.role,
        branch_id: editForm.branch_id || null,
        status: editForm.status,
      }, { headers });
      setEditModal(false);
      fetchAll();
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Gagal menyimpan perubahan');
    } finally {
      setSaving(false);
    }
  };

  // ── Reset Password ──
  const openReset = (u: User) => {
    setResetUser(u);
    setNewPassword('');
    setConfirmPassword('');
    setResetError('');
    setResetModal(true);
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) { setResetError('Password minimal 6 karakter'); return; }
    if (newPassword !== confirmPassword) { setResetError('Konfirmasi password tidak cocok'); return; }
    setResetting(true);
    setResetError('');
    try {
      await axios.patch(`${API}/users/${resetUser!.id}/reset-password`, { newPassword }, { headers });
      setResetModal(false);
      alert(`Password ${resetUser!.name} berhasil direset`);
    } catch (err: any) {
      setResetError(err?.response?.data?.error || 'Gagal reset password');
    } finally {
      setResetting(false);
    }
  };

  // ── Delete ──
  const handleDelete = async (u: User) => {
    if (!window.confirm(`Hapus pengguna "${u.name}" (${u.email})?\n\nTindakan ini tidak dapat dibatalkan.`)) return;
    try {
      await axios.delete(`${API}/users/${u.id}`, { headers });
      fetchAll();
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Gagal menghapus pengguna');
    }
  };

  // ── Filtered list ──
  const filtered = users.filter(u => {
    if (filterRole !== 'all' && u.role !== filterRole) return false;
    if (filterBranch !== 'all' && u.branch_id !== filterBranch) return false;
    return true;
  });

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>👥 Manajemen Pengguna</h1>
          <p style={s.subtitle}>Buat dan kelola akun Admin Cabang & Kasir</p>
        </div>
        <button style={s.createBtn} onClick={openCreate}>+ Buat Pengguna Baru</button>
      </div>

      {/* Filters */}
      <div style={s.filterRow}>
        <select style={s.select} value={filterRole} onChange={e => setFilterRole(e.target.value)}>
          <option value="all">Semua Role</option>
          <option value="super_admin">Super Admin</option>
          <option value="admin_branch">Admin Cabang</option>
          <option value="owner_branch">Owner Cabang</option>
          <option value="cashier">Kasir</option>
          <option value="finance">Finance</option>
          <option value="accounting">Accounting</option>
        </select>
        <select style={s.select} value={filterBranch} onChange={e => setFilterBranch(e.target.value)}>
          <option value="all">Semua Cabang</option>
          {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <span style={s.countBadge}>{filtered.length} pengguna</span>
      </div>

      {/* Table */}
      <div style={s.tableCard}>
        {loading ? (
          <div style={s.centered}><div style={s.spinner} /></div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead>
                <tr style={s.thead}>
                  <th style={s.th}>NAMA</th>
                  <th style={s.th}>EMAIL</th>
                  <th style={s.th}>NO HP</th>
                  <th style={s.th}>ROLE</th>
                  <th style={s.th}>CABANG</th>
                  <th style={{ ...s.th, textAlign: 'center' }}>STATUS</th>
                  <th style={{ ...s.th, textAlign: 'center' }}>LOGIN TERAKHIR</th>
                  <th style={{ ...s.th, textAlign: 'center' }}>AKSI</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 14 }}>
                      Tidak ada pengguna ditemukan
                    </td>
                  </tr>
                ) : filtered.map(u => (
                  <tr key={u.id} style={s.tr}>
                    <td style={{ ...s.td, fontWeight: 700, color: '#1e293b' }}>{u.name}</td>
                    <td style={{ ...s.td, color: '#64748b' }}>{u.email}</td>
                    <td style={{ ...s.td, color: '#64748b' }}>{u.phone || '—'}</td>
                    <td style={s.td}>
                      <span style={{ ...s.badge, color: ROLE_COLOR[u.role], background: ROLE_BG[u.role] }}>
                        {ROLE_LABEL[u.role] || u.role}
                      </span>
                    </td>
                    <td style={{ ...s.td, color: '#64748b' }}>{u.branch_name || '—'}</td>
                    <td style={{ ...s.td, textAlign: 'center' }}>
                      <span style={{
                        ...s.badge,
                        color: u.status === 'active' ? '#27AE60' : '#E74C3C',
                        background: u.status === 'active' ? '#EAFAF1' : '#FDEDEC',
                      }}>
                        {u.status === 'active' ? 'Aktif' : 'Nonaktif'}
                      </span>
                    </td>
                    <td style={{ ...s.td, textAlign: 'center', fontSize: 12, color: '#94a3b8' }}>
                      {u.last_login
                        ? new Date(u.last_login).toLocaleString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : '—'}
                    </td>
                    <td style={{ ...s.td, textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button style={s.editBtn} onClick={() => openEdit(u)}>✏️ Edit</button>
                        {u.role !== 'super_admin' && (
                          <button style={s.resetBtn} onClick={() => openReset(u)}>🔑 Reset PW</button>
                        )}
                        {u.id !== currentUser?.id && u.role !== 'super_admin' && (
                          <button style={s.deleteBtn} onClick={() => handleDelete(u)}>🗑 Hapus</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── CREATE MODAL ── */}
      {createModal && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <h2 style={s.modalTitle}>➕ Buat Pengguna Baru</h2>
            <p style={s.modalSub}>Admin Pusat membuat akun Admin Cabang atau Kasir</p>
            <form onSubmit={handleCreate} style={s.form}>
              <label style={s.label}>Nama Lengkap *</label>
              <input style={s.input} required placeholder="Nama lengkap" value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })} />

              <label style={s.label}>Email *</label>
              <input style={s.input} type="email" required placeholder="email@contoh.com" value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })} />

              <label style={s.label}>No HP</label>
              <input style={s.input} placeholder="08xxxxxxxxxx" value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })} />

              <label style={s.label}>Password *</label>
              <input style={s.input} type="password" required placeholder="Min. 6 karakter" value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })} />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={s.label}>Role *</label>
                  <select style={s.input} value={form.role}
                    onChange={e => setForm({ ...form, role: e.target.value as any, branch_id: '' })}>
                    <option value="cashier">Kasir</option>
                    <option value="admin_branch">Admin Cabang</option>
                    <option value="owner_branch">Owner Cabang</option>
                  </select>
                </div>
                {!['finance', 'accounting'].includes(form.role) && (
                  <div>
                    <label style={s.label}>
                      Cabang {(form.role === 'admin_branch' || form.role === 'owner_branch') ? '*' : ''}
                    </label>
                    <select style={s.input} value={form.branch_id}
                      onChange={e => setForm({ ...form, branch_id: e.target.value })}>
                      <option value="">— Pilih Cabang —</option>
                      {branches.map(b => <option key={b.id} value={b.id}>{b.name} ({b.city})</option>)}
                    </select>
                  </div>
                )}
              </div>

              {form.role === 'admin_branch' && (
                <div style={s.infoBox}>
                  💡 Admin Cabang wajib diassign ke cabang agar bisa mengelola data cabang tersebut.
                </div>
              )}
              {form.role === 'owner_branch' && (
                <div style={{ ...s.infoBox, background: '#FEF3C7', borderColor: '#D97706', color: '#92400E' }}>
                  👁️ Owner Cabang: akses baca saja — bisa melihat semua data cabang tapi tidak bisa mengubah, menambah, atau menghapus.
                </div>
              )}

              {createError && <div style={s.errorBox}>{createError}</div>}

              <div style={s.modalActions}>
                <button type="button" style={s.cancelBtn} onClick={() => setCreateModal(false)}>Batal</button>
                <button type="submit" style={s.saveBtn} disabled={creating}>
                  {creating ? 'Menyimpan...' : '✅ Buat Akun'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── EDIT MODAL ── */}
      {editModal && editUser && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <h2 style={s.modalTitle}>✏️ Edit Pengguna</h2>
            <p style={s.modalSub}>{editUser.email}</p>
            <form onSubmit={handleEdit} style={s.form}>
              <label style={s.label}>Nama Lengkap</label>
              <input style={s.input} value={editForm.name}
                onChange={e => setEditForm({ ...editForm, name: e.target.value })} />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={s.label}>Role</label>
                  <select style={s.input} value={editForm.role}
                    onChange={e => {
                      const newRole = e.target.value;
                      setEditForm({ ...editForm, role: newRole, branch_id: ['finance', 'accounting'].includes(newRole) ? '' : editForm.branch_id });
                    }}>
                    <option value="cashier">Kasir</option>
                    <option value="admin_branch">Admin Cabang</option>
                    <option value="owner_branch">Owner Cabang</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
                <div>
                  <label style={s.label}>Status</label>
                  <select style={s.input} value={editForm.status}
                    onChange={e => setEditForm({ ...editForm, status: e.target.value as any })}>
                    <option value="active">Aktif</option>
                    <option value="inactive">Nonaktif</option>
                  </select>
                </div>
              </div>

              {['finance', 'accounting'].includes(editForm.role) ? (
                <div style={{ ...s.infoBox, background: '#e0f2fe', borderColor: '#0891b2', color: '#0c4a6e' }}>
                  🌐 {editForm.role === 'finance' ? 'Finance' : 'Accounting'}: akses baca ke semua cabang — tidak perlu assign ke cabang tertentu.
                </div>
              ) : (
                <>
                  <label style={s.label}>Cabang</label>
                  <select style={s.input} value={editForm.branch_id}
                    onChange={e => setEditForm({ ...editForm, branch_id: e.target.value })}>
                    <option value="">— Tanpa Cabang —</option>
                    {branches.map(b => <option key={b.id} value={b.id}>{b.name} ({b.city})</option>)}
                  </select>
                </>
              )}

              <div style={s.modalActions}>
                <button type="button" style={s.cancelBtn} onClick={() => setEditModal(false)}>Batal</button>
                <button type="submit" style={s.saveBtn} disabled={saving}>
                  {saving ? 'Menyimpan...' : '💾 Simpan Perubahan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── RESET PASSWORD MODAL ── */}
      {resetModal && resetUser && (
        <div style={s.overlay}>
          <div style={{ ...s.modal, maxWidth: 400 }}>
            <h2 style={s.modalTitle}>🔑 Reset Password</h2>
            <p style={s.modalSub}>
              Atur password baru untuk <strong>{resetUser.name}</strong>
              {resetUser.branch_name ? ` — ${resetUser.branch_name}` : ''}
            </p>
            <form onSubmit={handleReset} style={s.form}>
              <label style={s.label}>Password Baru *</label>
              <input style={s.input} type="password" required placeholder="Min. 6 karakter"
                value={newPassword} onChange={e => setNewPassword(e.target.value)} />

              <label style={s.label}>Konfirmasi Password *</label>
              <input style={s.input} type="password" required placeholder="Ulangi password baru"
                value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />

              {resetError && <div style={s.errorBox}>{resetError}</div>}

              <div style={s.infoBox}>
                ⚠️ Setelah direset, sampaikan password baru ini langsung kepada {ROLE_LABEL[resetUser.role] || 'pengguna'} tersebut.
              </div>

              <div style={s.modalActions}>
                <button type="button" style={s.cancelBtn} onClick={() => setResetModal(false)}>Batal</button>
                <button type="submit" style={{ ...s.saveBtn, background: '#E74C3C' }} disabled={resetting}>
                  {resetting ? 'Mereset...' : '🔑 Reset Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  page: { padding: 24, background: '#F5F7FA', minHeight: '100vh' },

  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    background: '#fff', padding: '20px 24px', borderRadius: 12, marginBottom: 16,
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #E2E8F0',
  },
  title: { margin: '0 0 4px', fontSize: 22, fontWeight: 800, color: '#1e293b' },
  subtitle: { margin: 0, fontSize: 13, color: '#64748b' },
  createBtn: {
    padding: '10px 20px', background: '#1A73E8', color: '#fff',
    border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700,
    whiteSpace: 'nowrap',
  },

  filterRow: {
    display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap',
  },
  select: {
    padding: '8px 12px', border: '1px solid #E2E8F0', borderRadius: 8,
    fontSize: 13, color: '#374151', background: '#fff', cursor: 'pointer',
  },
  countBadge: {
    fontSize: 13, color: '#64748b', fontWeight: 600,
  },

  tableCard: {
    background: '#fff', borderRadius: 12, overflow: 'hidden',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #E2E8F0',
  },
  centered: { display: 'flex', justifyContent: 'center', padding: 40 },
  spinner: {
    width: 32, height: 32, border: '3px solid #E2E8F0',
    borderTop: '3px solid #1A73E8', borderRadius: '50%',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: '#F8FAFC' },
  th: {
    padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700,
    color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5,
    borderBottom: '2px solid #E2E8F0',
  },
  tr: { borderBottom: '1px solid #F1F5F9' },
  td: { padding: '12px 16px', fontSize: 13, color: '#374151' },
  badge: {
    display: 'inline-block', padding: '3px 10px', borderRadius: 20,
    fontSize: 11, fontWeight: 700,
  },
  editBtn: {
    padding: '5px 10px', background: '#EEF4FF', border: '1px solid #BFDBFE',
    borderRadius: 6, cursor: 'pointer', fontSize: 12, color: '#1A73E8', fontWeight: 600,
  },
  resetBtn: {
    padding: '5px 10px', background: '#FEF9E7', border: '1px solid #FDE68A',
    borderRadius: 6, cursor: 'pointer', fontSize: 12, color: '#D97706', fontWeight: 600,
  },
  deleteBtn: {
    padding: '5px 10px', background: '#FEF2F2', border: '1px solid #FECACA',
    borderRadius: 6, cursor: 'pointer', fontSize: 12, color: '#DC2626', fontWeight: 600,
  },

  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 520,
    maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  },
  modalTitle: { margin: '0 0 4px', fontSize: 18, fontWeight: 800, color: '#1e293b' },
  modalSub: { margin: '0 0 20px', fontSize: 13, color: '#64748b' },
  form: { display: 'flex', flexDirection: 'column', gap: 8 },
  label: { fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 2 },
  input: {
    padding: '10px 12px', border: '1.5px solid #E2E8F0', borderRadius: 8,
    fontSize: 14, color: '#333', outline: 'none', fontFamily: 'inherit',
  },
  infoBox: {
    background: '#EEF4FF', border: '1px solid #BFDBFE', borderRadius: 8,
    padding: '10px 14px', fontSize: 13, color: '#1e40af', lineHeight: 1.5,
  },
  errorBox: {
    background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8,
    padding: '10px 14px', fontSize: 13, color: '#DC2626',
  },
  modalActions: { display: 'flex', gap: 10, marginTop: 8 },
  cancelBtn: {
    flex: 1, padding: '11px 0', background: '#F1F5F9', border: '1px solid #E2E8F0',
    borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#64748b',
  },
  saveBtn: {
    flex: 2, padding: '11px 0', background: '#1A73E8', border: 'none',
    borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#fff',
  },
};
